/**
 * This file provides helpers to access/modify ShareDB documents in worksheet format.
 * Helpers for the worksheet format are defined in `constants.ts`. The difference with
 * this file is that it provides functions with side effects, and mainly functions that
 * directly interface with an instantiated "Doc" class from ShareDB.
 */

import { cloneDeep, get, isEmpty } from "lodash-es";
import type { Doc, Op, ShareDBSourceOptions } from "sharedb/lib/client";

import { LINKED_SECTION_SEEDING_ACCESS_ID, isLearnerAccessId } from "../access-id";
import type {
  AssetBlockType,
  AssetFieldType,
  BlockType,
  CodeBlockType,
  EmbedBlockType,
  FieldDataType,
  FieldType,
  FileBlockType,
  FreeResponseBlockType,
  InstructionsBlockType,
  MultipleChoiceBlockType,
  PathType,
  SeededCellType,
  TableBlockType,
  WhiteboardBlockType,
  WorksheetType,
} from "../types/worksheet";
import { createId } from "../utils";

import {
  ASSET_BLOCK,
  BLOCKS_PATH,
  DATA_PATH,
  DEFAULT_BLOCKS,
  MAX_BLOCK_COUNT,
  STATIC_CONTENT_BLOCK_TYPES,
} from "./constants";

export const hasCodeCells = (doc: Doc<WorksheetType> | undefined) =>
  doc?.data?.b.some((block) => block.t === "Code");

export const isEditableBlock = (block: BlockType) => !STATIC_CONTENT_BLOCK_TYPES.includes(block.t);

export const getEditableBlocks = (doc: Doc<WorksheetType> | undefined) =>
  doc?.data?.b.filter(isEditableBlock);

export const hasEditableBlocks = (doc: Doc<WorksheetType> | undefined | null) =>
  doc?.data?.b.some(isEditableBlock);

export const hasBlocks = (doc: Doc<WorksheetType>) => doc?.data?.b.length > 0;

export const blockIsAsset = (
  doc: Doc<WorksheetType> | undefined,
  blockId: string | null | undefined,
) => {
  const block = doc?.data?.b?.find((b) => b.id === blockId);
  return blockId && block?.t === ASSET_BLOCK;
};

/**
 * This can be used with any ShareDB operation to return a promise instead of using
 * the callback. This prompts the linter to show an error if the promise is not
 * "caught" correctly. We also have a general error handler that responds to all
 * submitOp errors in ShareDBDoc.tsx, but using this pattern we ensure that local
 * calling functions can respond to errors _in addition to_ the app in general.
 */
export const submitOpPromise = (
  doc: Doc<WorksheetType>,
  ops: Op | Op[],
  options: ShareDBSourceOptions = {},
) => {
  return new Promise((resolve, reject) => {
    doc.submitOp(ops, options, (err) => {
      if (err) {
        // eslint-disable-next-line @typescript-eslint/prefer-promise-reject-errors
        return reject(err);
      }
      resolve(true);
    });
  });
};

export function createBlockOfType(type: "Asset"): AssetBlockType & { id: string };
export function createBlockOfType(type: "Code"): CodeBlockType & { id: string };
export function createBlockOfType(type: "Embed"): EmbedBlockType & { id: string };
export function createBlockOfType(type: "File"): FileBlockType & { id: string };
export function createBlockOfType(type: "FreeResponse"): FreeResponseBlockType & { id: string };
export function createBlockOfType(type: "Instructions"): InstructionsBlockType & { id: string };
export function createBlockOfType(type: "MultipleChoice"): MultipleChoiceBlockType & { id: string };
export function createBlockOfType(type: "Table"): TableBlockType & { id: string };
export function createBlockOfType(type: "Whiteboard"): WhiteboardBlockType & { id: string };
export function createBlockOfType(type: BlockType["t"]): BlockType;
export function createBlockOfType(type: BlockType["t"]) {
  const block = cloneDeep(DEFAULT_BLOCKS[type]) as BlockType;
  block.id = createId();
  block.f = block.f.map((component: FieldType) => ({
    ...component,
    id: createId(),
  })) as Extract<FieldType, "field">;
  return block;
}

export function getUserData(doc: Doc<WorksheetType>) {
  return get(doc.data || {}, DATA_PATH, {});
}

export function getBlocks(doc: Doc<WorksheetType>) {
  return get(doc.data || {}, BLOCKS_PATH, []) as BlockType[];
}

/**
 * ShareDB ops to list-delete the block at `blocks[blockIndex]` and delete matching learner
 * payloads under `d` for each field id in that block (`od` on `d.<accessId>.<fieldId>`).
 *
 * `blocks` must be the block array state **as indexed when this delete runs** (after any prior
 * list ops in the same batch have been applied to your simulated copy, since each `ld` shifts
 * indices). `userData` is the `d` subtree used for field cleanup (e.g. `getUserData(doc)`).
 */
export const buildDeleteBlockOps = (
  blocks: BlockType[],
  blockIndex: number,
  userData: FieldDataType,
): Op[] => {
  const resolvedBlock = blocks[blockIndex];
  if (!resolvedBlock) {
    throw new Error(`delete block: invalid index ${blockIndex}`);
  }
  return [
    { p: [BLOCKS_PATH, blockIndex], ld: resolvedBlock },
    // Ops to wipe student content referencing fields that this block contains
    ...resolvedBlock.f
      .map((field: FieldType) =>
        Object.keys(userData).map(
          (accessId) =>
            userData[accessId] &&
            userData[accessId][field.id] && {
              p: [DATA_PATH, accessId, field.id],
              od: userData[accessId][field.id],
            },
        ),
      )
      .flat()
      // Block component might not have student content, like for instruction text
      .filter((op): op is NonNullable<typeof op> & { od: unknown } => !!op && !!op.od),
  ];
};

export const deleteBlockFromWorksheet = async (doc: Doc<WorksheetType>, blockIndex: number) => {
  return submitOpPromise(doc, buildDeleteBlockOps(getBlocks(doc), blockIndex, getUserData(doc)));
};

/**
 * True when any class / group / individual learner path under `d` has non-empty data for a
 * current field id. Instructor (`i`) and linked-section seeding (`s`) are ignored.
 */
export const worksheetHasStudentResponseData = (doc: Doc<WorksheetType>) => {
  const blocks = getBlocks(doc);
  const userData = getUserData(doc);
  if (!blocks.length || !userData) {
    return false;
  }
  const fieldIds = new Set(
    blocks.flatMap((block) => (block.f as FieldType[]).map((field) => field.id)),
  );
  for (const [accessId, perField] of Object.entries(userData)) {
    if (isLearnerAccessId(accessId) && perField) {
      for (const [fieldId, payload] of Object.entries(perField)) {
        if (fieldIds.has(fieldId) && !isEmpty(payload)) {
          return true;
        }
      }
    }
  }
  return false;
};

export const instructorFieldIsInitialized = (doc: Doc<WorksheetType>, fieldId: string): boolean => {
  const userData = get(doc.data, [
    DATA_PATH,
    LINKED_SECTION_SEEDING_ACCESS_ID,
    fieldId,
  ]) as SeededCellType;

  return Boolean(userData?.initialized);
};

export const maybeAddInitializationWithOp = (
  doc: Doc<WorksheetType>,
  op: Op | Op[],
  path: PathType,
) => {
  const ops = Array.isArray(op) ? op : [op];

  if (path[1] === LINKED_SECTION_SEEDING_ACCESS_ID) {
    // For instructor seeding, we need to know if the instructor changed the content.
    const pathToField = path.slice(0, 3);
    const currentData = get(doc.data, pathToField) as SeededCellType;
    if (!currentData?.initialized) {
      ops.unshift({
        p: [...pathToField, "initialized"],
        oi: true,
      });
    }
  }

  return ops;
};

export const addAssetToDoc = (
  doc: Doc<WorksheetType>,
  assetData: Partial<AssetFieldType>,
  insertAtIndex = MAX_BLOCK_COUNT,
) => {
  const block = createBlockOfType("Asset");
  const bc = block.f[0];
  Object.assign(bc, assetData);
  return submitOpPromise(doc, [{ p: [BLOCKS_PATH, insertAtIndex], li: block }]);
};

export const fieldIdsFromDoc = (doc: Doc<WorksheetType>) => {
  const blocks = get(doc.data, BLOCKS_PATH);
  return blocks.flatMap((block) => block.f.map((field: { id: string }) => field.id));
};

export const accessIdHasData = (doc: Doc<WorksheetType>, accessId: string) => {
  const dataForAccessId = get(doc.data, [DATA_PATH, accessId]);
  if (!dataForAccessId) return false;
  // We have *some* kind of object, but is it meaningful?
  const fieldIds = fieldIdsFromDoc(doc);
  const dataFieldIds = Object.keys(dataForAccessId).filter((key) => {
    // We only care about data that is associated with fields that are currently in the doc.
    // This will filter out data associated with fields that have been deleted.
    return fieldIds.includes(key);
  });
  // If any of these fields have data, then we return true.
  return dataFieldIds.some((fieldId) => {
    const fieldData = dataForAccessId[fieldId];
    return !isEmpty(fieldData);
  });
};
